/**
 * NGAP Clinical Intention Configuration (#65)
 *
 * This file centralises the NGAP act code mappings used by SmartNGAPPanel's
 * clinical suggestion buttons. If backend seed data changes codes, update
 * only this file instead of hunting through component code.
 */

export interface ClinicalIntention {
  id: string;
  label: string;
  icon: string;
  color: string;
  description: string;
  /** NGAP act codes that belong to this clinical intention */
  matchCodes: string[];
}

/**
 * Ordered list of clinical intentions shown as smart-suggestion buttons.
 * Each maps to one or more NGAP act codes from the catalog seed data.
 */
export const CLINICAL_INTENTIONS: ClinicalIntention[] = [
  {
    id: 'obturation',
    label: 'Obturation',
    icon: '■',
    color: '#22c55e',
    description: 'Restauration adaptée au nombre de surfaces',
    matchCodes: ['D700', 'D701', 'D702'],
  },
  {
    id: 'endodontie',
    label: 'Endodontie',
    icon: '↓',
    color: '#8b5cf6',
    description: 'Traitement canalaire adapté au type de dent',
    matchCodes: ['D703', 'D704', 'D705', 'D706'],
  },
  {
    id: 'extraction',
    label: 'Extraction',
    icon: '✕',
    color: '#ef4444',
    description: '1ère ou suivante selon la séance',
    matchCodes: ['D713', 'D714', 'D715'],
  },
  {
    id: 'sagesse',
    label: 'Extraction sagesse',
    icon: '⚡',
    color: '#f97316',
    description: 'Dent de sagesse incluse / semi-incluse',
    matchCodes: ['D720', 'D725'],
  },
  {
    id: 'couronne',
    label: 'Couronne',
    icon: '♛',
    color: '#eab308',
    description: 'Provisoire, céramo-métallique, etc.',
    matchCodes: ['D748', 'D900', 'D901', 'D902'],
  },
  {
    id: 'prothese',
    label: 'Prothèse amovible',
    icon: '◇',
    color: '#64748b',
    description: 'Prothèse partielle selon le nombre de dents',
    matchCodes: ['D761', 'D762', 'D763', 'D764', 'D765', 'D766', 'D767', 'D768', 'D769', 'D770', 'D771', 'D772'],
  },
  {
    id: 'detartrage',
    label: 'Détartrage',
    icon: '≋',
    color: '#fbbf24',
    description: 'Nettoyage et détartrage',
    matchCodes: ['D708'],
  },
  {
    id: 'scellement',
    label: 'Scellement',
    icon: '◎',
    color: '#06b6d4',
    description: 'Scellement prophylactique des sillons',
    matchCodes: ['D712'],
  },
  {
    id: 'ancrage',
    label: 'Ancrage radiculaire',
    icon: '⇑',
    color: '#7c3aed',
    description: 'Restauration avec inlay-core',
    matchCodes: ['D707', 'D903'],
  },
];

/**
 * Quick-lookup set of all configured NGAP codes.
 * Useful for detecting whether an act code belongs to any clinical intention.
 */
export const ALL_CLINICAL_CODES = new Set(
  CLINICAL_INTENTIONS.flatMap(i => i.matchCodes)
);
